import { useCallback, useEffect, useRef, MutableRefObject } from 'react';
import type Konva from 'konva';
import { snapshotFromStore } from '../stores/undoStore';
import { CANVAS } from '../constants';
import type { Node, Edge, NodeGroup, SequenceFragment, SequenceParticipant, SequenceMessage, DiagramType, KonvaMouseEvent, KonvaWheelEvent, Point } from '../types';
import type { CanvasDrawTool } from '../stores/uiStore';
import { useUIStore } from '../stores/uiStore';
import { idsInPolygon, idsInRect, pointInPolygon } from '../utils/selectionGeometry';

const SCALE_MIN = CANVAS.SCALE_MIN;
const SCALE_MAX = CANVAS.SCALE_MAX;

export interface CanvasHandlersConfig {
  viewport: { x: number; y: number; scale: number };
  minScale?: number;
  spaceHeld: boolean;
  isFrameDrawMode: boolean;
  canvasDrawTool: CanvasDrawTool;
  canvasStampType?: string | null;
  isPanning: boolean;
  activeFlowId: string | null;
  isReadOnly: boolean;
  connectionState: { isConnecting: boolean };
  diagramType: DiagramType;
  nodes: Node[];
  edges: Edge[];
  groups: NodeGroup[];
  participants: SequenceParticipant[];
  messages: SequenceMessage[];
  fragments: SequenceFragment[];
  marqueeRect: { x: number; y: number; w: number; h: number } | null;
  lassoPoints: number[] | null;
  frameDrawRect: { x: number; y: number; w: number; h: number } | null;
  markupStroke: { x1: number; y1: number; x2: number; y2: number } | null;
  sequenceDrag: { sourceParticipantId: string; sourceX: number; startY: number; currentX?: number } | null;

  setIsPanning: (v: boolean) => void;
  setMarqueeRect: (v: { x: number; y: number; w: number; h: number } | null) => void;
  setLassoPoints: (v: number[] | null) => void;
  setFrameDrawRect: (v: { x: number; y: number; w: number; h: number } | null) => void;
  setMarkupStroke: (v: { x1: number; y1: number; x2: number; y2: number } | null) => void;
  setIsFrameDrawMode: (v: boolean) => void;
  setCanvasDrawTool: (v: CanvasDrawTool) => void;
  setSequenceDrag: (v: { sourceParticipantId: string; sourceX: number; startY: number; currentX?: number } | null) => void;
  setQuickNodeCreation: (v: { show: boolean; screenX: number; screenY: number } | null) => void;
  setPendingEditY: (v: number | null) => void;
  setAnchorNodeId: (v: string | null) => void;

  panStart: MutableRefObject<{ sx: number; sy: number; screenX: number; screenY: number; vpX: number; vpY: number } | null>;
  leftPanPending: MutableRefObject<{ sx: number; sy: number; screenX: number; screenY: number; vpX: number; vpY: number } | null>;
  canvasMouseDownPos: MutableRefObject<{ x: number; y: number } | null>;
  marqueeStart: MutableRefObject<{ x: number; y: number } | null>;
  frameDrawStart: MutableRefObject<{ x: number; y: number } | null>;
  bKeyHeld: MutableRefObject<boolean>;
  cursorCanvasPos: MutableRefObject<{ x: number; y: number }>;

  updateViewport: (v: Partial<{ x: number; y: number; scale: number }>) => void;
  updateTempEdge: (x: number, y: number) => void;
  selectItem: (id: string, multi?: boolean) => void;
  clearSelection: () => void;
  createFrame: (pos: { x: number; y: number }, size: { width: number; height: number }, nodeIds: string[], groupIds: string[]) => string;
  onFrameCreated?: (frameId: string) => void;
  onMarkupStrokeComplete?: (kind: 'arrow' | 'line', start: Point, end: Point) => void;
  onMarkupTextPlace?: (position: Point) => void;
  onStampDrawn?: (type: string, rect: { x: number; y: number; w: number; h: number }) => void;
  addMessage: (msg: Omit<SequenceMessage, 'id'>) => void;

  toCanvas: (sx: number, sy: number) => { x: number; y: number };
}

export function useCanvasHandlers(cfg: CanvasHandlersConfig) {
  const {
    viewport, minScale = SCALE_MIN, spaceHeld, isFrameDrawMode, canvasDrawTool, canvasStampType = null, isPanning, activeFlowId, isReadOnly,
    connectionState, diagramType, nodes, edges, groups, participants, messages, fragments,
    marqueeRect, lassoPoints, frameDrawRect, markupStroke, sequenceDrag,
    setIsPanning, setMarqueeRect, setLassoPoints, setFrameDrawRect, setMarkupStroke,
    setSequenceDrag, setQuickNodeCreation, setPendingEditY, setAnchorNodeId,
    panStart, leftPanPending, marqueeStart, frameDrawStart, bKeyHeld, cursorCanvasPos,
    canvasMouseDownPos,
    updateViewport, updateTempEdge, selectItem, clearSelection, createFrame, onFrameCreated,
    onMarkupStrokeComplete, onMarkupTextPlace, onStampDrawn, addMessage,
    toCanvas,
  } = cfg;
  const panLockElementRef = useRef<HTMLElement | null>(null);

  const releasePanPointerLock = useCallback(() => {
    const lockElement = panLockElementRef.current;
    if (lockElement && document.pointerLockElement === lockElement && document.exitPointerLock) {
      document.exitPointerLock();
    }
    panLockElementRef.current = null;
  }, []);

  const requestPanPointerLock = useCallback((stage: Konva.Stage | null | undefined) => {
    const container = stage?.container?.();
    if (!(container instanceof HTMLElement) || typeof container.requestPointerLock !== 'function') {
      panLockElementRef.current = null;
      return;
    }

    panLockElementRef.current = container;
    try {
      container.requestPointerLock();
    } catch {
      panLockElementRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!isPanning) return;

    const onWindowMove = (event: MouseEvent) => {
      const pan = panStart.current;
      if (!pan) return;

      const isPointerLocked =
        panLockElementRef.current !== null && document.pointerLockElement === panLockElementRef.current;

      if (isPointerLocked) {
        if (event.movementX === 0 && event.movementY === 0) return;
        pan.vpX += event.movementX;
        pan.vpY += event.movementY;
        updateViewport({ x: pan.vpX, y: pan.vpY });
        return;
      }

      const nextScreenX = typeof event.screenX === 'number' ? event.screenX : pan.screenX;
      const nextScreenY = typeof event.screenY === 'number' ? event.screenY : pan.screenY;
      const deltaX = nextScreenX - pan.screenX;
      const deltaY = nextScreenY - pan.screenY;
      if (deltaX === 0 && deltaY === 0) return;

      pan.screenX = nextScreenX;
      pan.screenY = nextScreenY;
      pan.vpX += deltaX;
      pan.vpY += deltaY;
      updateViewport({
        x: pan.vpX,
        y: pan.vpY,
      });
    };

    const onWindowUp = () => {
      setIsPanning(false);
      panStart.current = null;
      releasePanPointerLock();
    };

    window.addEventListener('mousemove', onWindowMove, true);
    window.addEventListener('mouseup', onWindowUp, true);

    return () => {
      window.removeEventListener('mousemove', onWindowMove, true);
      window.removeEventListener('mouseup', onWindowUp, true);
    };
  }, [isPanning, panStart, releasePanPointerLock, setIsPanning, updateViewport]);

  useEffect(() => () => {
    releasePanPointerLock();
  }, [releasePanPointerLock]);

  const handleWheel = useCallback((e: KonvaWheelEvent) => {
    e.evt.preventDefault();
    const stage = e.target.getStage();
    const pointer = stage?.getPointerPosition();
    if (!pointer) return;
    const old = viewport.scale;
    // Standardize delta to avoid erratic scrolling across trackpads/mice
    const scrollDirection = Math.sign(e.evt.deltaY);
    // Use a subtle base factor to make zoom smooth and consistent
    const zoomFactor = 1 - scrollDirection * 0.04;
    const next = Math.min(SCALE_MAX, Math.max(minScale, old * zoomFactor));
    const cx = (pointer.x - viewport.x) / old;
    const cy = (pointer.y - viewport.y) / old;
    updateViewport({ scale: next, x: pointer.x - cx * next, y: pointer.y - cy * next });
  }, [viewport, updateViewport, minScale]);

  const handleStageMouseDown = useCallback((e: KonvaMouseEvent) => {
    const placingType = canvasStampType ?? (canvasDrawTool === 'box' ? 'box' : null);
    const drawTool = canvasDrawTool !== 'none' ? canvasDrawTool : (isFrameDrawMode ? 'frame' : 'none');
    const isStrokeTool = drawTool === 'arrow' || drawTool === 'line';
    const isBoxDrawTool = drawTool === 'frame' || placingType !== null;
    const isDrawTool = isStrokeTool || isBoxDrawTool;

    const isMiddle = e.evt.button === 1;
    const isSpace = spaceHeld && e.evt.button === 0;
    const isPlainLeftOnStage = e.evt.button === 0
      && !e.evt.shiftKey && !spaceHeld
      && !connectionState.isConnecting && !isDrawTool
      && canvasDrawTool !== 'text'
      && e.target === e.target.getStage();
    const isLassoOnStage = e.evt.button === 0
      && e.evt.shiftKey && (e.evt.ctrlKey || e.evt.metaKey)
      && e.target === e.target.getStage()
      && !connectionState.isConnecting;
    const isShiftOnStage = e.evt.button === 0
      && e.evt.shiftKey && !e.evt.ctrlKey && !e.evt.metaKey
      && e.target === e.target.getStage()
      && !connectionState.isConnecting;
    const isPlainLeftDraw = e.evt.button === 0
      && !e.evt.shiftKey && !spaceHeld
      && !isReadOnly
      && !connectionState.isConnecting && isDrawTool
      && e.target === e.target.getStage();

    if (isMiddle || isSpace) {
      e.evt.preventDefault();
      const stage = e.target.getStage();
      const pointer = stage?.getPointerPosition();
      if (!pointer) return;
      setIsPanning(true);
      panStart.current = {
        sx: pointer.x,
        sy: pointer.y,
        screenX: typeof e.evt.screenX === 'number' ? e.evt.screenX : pointer.x,
        screenY: typeof e.evt.screenY === 'number' ? e.evt.screenY : pointer.y,
        vpX: viewport.x,
        vpY: viewport.y,
      };
      requestPanPointerLock(stage);
    } else if (isPlainLeftOnStage) {
      const pointer = e.target.getStage()?.getPointerPosition();
      if (!pointer) return;
      leftPanPending.current = {
        sx: pointer.x,
        sy: pointer.y,
        screenX: typeof e.evt.screenX === 'number' ? e.evt.screenX : pointer.x,
        screenY: typeof e.evt.screenY === 'number' ? e.evt.screenY : pointer.y,
        vpX: viewport.x,
        vpY: viewport.y,
      };
      canvasMouseDownPos.current = { x: pointer.x, y: pointer.y };
    } else if (isPlainLeftDraw) {
      const pointer = e.target.getStage()?.getPointerPosition();
      if (!pointer) return;
      frameDrawStart.current = toCanvas(pointer.x, pointer.y);
    } else if (isLassoOnStage) {
      e.evt.preventDefault();
      const pointer = e.target.getStage()?.getPointerPosition();
      if (!pointer) return;
      const c = toCanvas(pointer.x, pointer.y);
      setLassoPoints([c.x, c.y]);
    } else if (isShiftOnStage) {
      const pointer = e.target.getStage()?.getPointerPosition();
      if (!pointer) return;
      const c = toCanvas(pointer.x, pointer.y);
      if (isBoxDrawTool) {
        frameDrawStart.current = { x: c.x, y: c.y };
      } else {
        marqueeStart.current = { x: c.x, y: c.y };
      }
    }
  }, [spaceHeld, viewport.x, viewport.y, connectionState.isConnecting, requestPanPointerLock, toCanvas, isFrameDrawMode, canvasDrawTool, canvasStampType, isReadOnly, setLassoPoints]);

  const handleCanvasMouseMove = useCallback((e: KonvaMouseEvent) => {
    const stage = e.target.getStage();
    const pointer = stage?.getPointerPosition();
    if (!pointer) return;
    const c = toCanvas(pointer.x, pointer.y);
    cursorCanvasPos.current = c;

    if (leftPanPending.current && !isPanning) {
      const dx = pointer.x - leftPanPending.current.sx;
      const dy = pointer.y - leftPanPending.current.sy;
      if (dx * dx + dy * dy > 25) {
        panStart.current = leftPanPending.current;
        leftPanPending.current = null;
        setIsPanning(true);
        requestPanPointerLock(stage);
      }
    }

    if (isPanning) {
      return;
    }

    if (frameDrawStart.current) {
      const drawTool = canvasDrawTool !== 'none' ? canvasDrawTool : (isFrameDrawMode ? 'frame' : 'none');
      if (drawTool === 'arrow' || drawTool === 'line') {
        setMarkupStroke({
          x1: frameDrawStart.current.x,
          y1: frameDrawStart.current.y,
          x2: c.x,
          y2: c.y,
        });
      } else {
        setFrameDrawRect({
          x: Math.min(frameDrawStart.current.x, c.x),
          y: Math.min(frameDrawStart.current.y, c.y),
          w: Math.abs(c.x - frameDrawStart.current.x),
          h: Math.abs(c.y - frameDrawStart.current.y),
        });
      }
      return;
    }

    if (lassoPoints) {
      const lastX = lassoPoints[lassoPoints.length - 2];
      const lastY = lassoPoints[lassoPoints.length - 1];
      if ((c.x - lastX) ** 2 + (c.y - lastY) ** 2 >= 36) {
        setLassoPoints([...lassoPoints, c.x, c.y]);
      }
      return;
    }

    if (marqueeStart.current) {
      setMarqueeRect({
        x: Math.min(marqueeStart.current.x, c.x),
        y: Math.min(marqueeStart.current.y, c.y),
        w: Math.abs(c.x - marqueeStart.current.x),
        h: Math.abs(c.y - marqueeStart.current.y),
      });
      return;
    }

    if (connectionState.isConnecting && !isReadOnly) updateTempEdge(c.x, c.y);
    if (sequenceDrag && !isReadOnly) setSequenceDrag({ ...sequenceDrag, currentX: c.x });
  }, [isPanning, connectionState.isConnecting, requestPanPointerLock, updateTempEdge, toCanvas, sequenceDrag, isReadOnly, canvasDrawTool, isFrameDrawMode, lassoPoints, setLassoPoints]);

  const handleCanvasMouseUp = useCallback((e: KonvaMouseEvent) => {
    leftPanPending.current = null;

    if (isPanning) {
      setIsPanning(false);
      panStart.current = null;
      releasePanPointerLock();
      return;
    }

    if (!isReadOnly && markupStroke && frameDrawStart.current) {
      const start = { x: markupStroke.x1, y: markupStroke.y1 };
      const end = { x: markupStroke.x2, y: markupStroke.y2 };
      const dx = end.x - start.x;
      const dy = end.y - start.y;
      const kind = 'arrow';
      if (dx * dx + dy * dy > 64) {
        onMarkupStrokeComplete?.(kind, start, end);
        useUIStore.getState().consumeOneShotDraw();
      }
      frameDrawStart.current = null;
      setMarkupStroke(null);
      bKeyHeld.current = false;
      return;
    }

    if (!isReadOnly && (canvasDrawTool === 'box' || canvasStampType) && frameDrawStart.current) {
      const start = frameDrawStart.current;
      const { x, y, w, h } = frameDrawRect ?? { x: start.x, y: start.y, w: 0, h: 0 };
      const tiny = w < 12 && h < 12;
      const type = canvasStampType ?? 'box';
      onStampDrawn?.(type, tiny
        ? { x: start.x, y: start.y, w: 0, h: 0 }
        : { x, y, w, h });
      frameDrawStart.current = null;
      setFrameDrawRect(null);
      bKeyHeld.current = false;
      useUIStore.getState().consumeOneShotDraw();
      return;
    }

    if (!isReadOnly && frameDrawRect && frameDrawStart.current) {
      const { x, y, w, h } = frameDrawRect;
      if (w > 20 && h > 20) {
        const capturedNodeIds = nodes
          .filter(n =>
            n.position.x + n.size.width / 2 > x && n.position.x + n.size.width / 2 < x + w &&
            n.position.y + n.size.height / 2 > y && n.position.y + n.size.height / 2 < y + h &&
            !n.parentGroupId
          )
          .map((n: Node) => n.id);
        const capturedGroupIds = groups
          .filter(g =>
            !g.isFrame && !g.parentGroupId &&
            g.position.x + g.size.width / 2 > x && g.position.x + g.size.width / 2 < x + w &&
            g.position.y + g.size.height / 2 > y && g.position.y + g.size.height / 2 < y + h
          )
          .map((g: NodeGroup) => g.id);
        snapshotFromStore();
        const newFrameId = createFrame({ x, y }, { width: w, height: h }, capturedNodeIds, capturedGroupIds);
        if (onFrameCreated) onFrameCreated(newFrameId);
      }
      frameDrawStart.current = null;
      setFrameDrawRect(null);
      useUIStore.getState().consumeOneShotDraw();
      bKeyHeld.current = false;
      return;
    }

    if (lassoPoints && lassoPoints.length >= 6) {
      if (diagramType !== 'sequence') {
        idsInPolygon(lassoPoints, nodes, edges, groups).forEach(id => selectItem(id, true));
      } else {
        participants.forEach(p => {
          if (pointInPolygon(p.x + 70, 40, lassoPoints)) selectItem(p.id, true);
        });
        messages.forEach(m => {
          if (pointInPolygon(160, m.y, lassoPoints)) selectItem(m.id, true);
        });
      }
      setLassoPoints(null);
      return;
    }
    if (lassoPoints) {
      setLassoPoints(null);
      return;
    }

    if (marqueeRect && marqueeStart.current) {
      if (diagramType !== 'sequence') {
        idsInRect(marqueeRect, nodes, edges, groups).forEach(id => selectItem(id, true));
      } else {
        const { x, y, w, h } = marqueeRect;
        participants.filter(p => p.x > x && p.x < x + w).forEach(p => selectItem(p.id, true));
        messages.filter(m => m.y > y && m.y < y + h).forEach(m => selectItem(m.id, true));
      }
      marqueeStart.current = null;
      setMarqueeRect(null);
      return;
    }

    if (!isReadOnly && connectionState.isConnecting && e.target === e.target.getStage()) {
      const pointer = e.target.getStage().getPointerPosition();
      if (pointer) setQuickNodeCreation({ show: true, screenX: pointer.x, screenY: pointer.y });
    }

    if (!isReadOnly && sequenceDrag) {
      if (sequenceDrag.currentX !== undefined) {
        const SNAP_RANGE = 60;
        const nearestP = participants.reduce<{ p: SequenceParticipant; dist: number } | null>((best, p) => {
          if (p.id === sequenceDrag.sourceParticipantId) return best;
          const cx = p.x + 70;
          const dist = Math.abs(sequenceDrag.currentX! - cx);
          if (dist < SNAP_RANGE && (!best || dist < best.dist)) return { p, dist };
          return best;
        }, null);
        if (nearestP) {
          snapshotFromStore();
          const snapContainingFrag = fragments.find((f: SequenceFragment) => sequenceDrag.startY > f.startY && sequenceDrag.startY < f.endY);
          addMessage({
            sourceId: sequenceDrag.sourceParticipantId,
            targetId: nearestP.p.id,
            y: sequenceDrag.startY,
            label: '',
            type: 'sync',
            fragmentId: snapContainingFrag?.id,
          });
          setPendingEditY(sequenceDrag.startY);
        }
      }
      setSequenceDrag(null);
    }
  }, [isPanning, marqueeRect, lassoPoints, frameDrawRect, markupStroke, connectionState.isConnecting, releasePanPointerLock, sequenceDrag, isReadOnly,
      participants, diagramType, messages, nodes, edges, groups, addMessage, selectItem, fragments, createFrame, canvasDrawTool, canvasStampType, onMarkupStrokeComplete, onStampDrawn, setLassoPoints]);

  const handleCanvasClick = useCallback((e: KonvaMouseEvent) => {
    if (e.target !== e.target.getStage()) return;
    if (activeFlowId) return;
    if (!isReadOnly && canvasDrawTool === 'text') {
      const pointer = e.target.getStage()?.getPointerPosition();
      if (pointer) onMarkupTextPlace?.(toCanvas(pointer.x, pointer.y));
      useUIStore.getState().consumeOneShotDraw();
      return;
    }
    if (!isReadOnly && (canvasDrawTool === 'box' || canvasStampType)) {
      return;
    }
    // Only deselect if this was a true stationary click (no drag movement)
    const downPos = canvasMouseDownPos.current;
    canvasMouseDownPos.current = null;
    if (downPos) {
      const pointer = e.target.getStage().getPointerPosition();
      if (pointer) {
        const dx = pointer.x - downPos.x;
        const dy = pointer.y - downPos.y;
        if (dx * dx + dy * dy > 9) return; // moved >3px — was a pan, don't deselect
      }
    }
    if (!e.evt.shiftKey) { clearSelection(); setAnchorNodeId(null); }
  }, [clearSelection, activeFlowId, canvasMouseDownPos, canvasDrawTool, canvasStampType, isReadOnly, onMarkupTextPlace, toCanvas]);

  return { handleWheel, handleStageMouseDown, handleCanvasMouseMove, handleCanvasMouseUp, handleCanvasClick };
}
